import { useEffect, useState } from 'react';
import { formatCodeForDisplay, normaliseFriendCode } from '../game/codes';
import { useGame } from '../state/GameProvider';
import { Leaderboard } from './Leaderboard';

/**
 * Friends: a standing board of lifetime scores, for people who have swapped codes.
 *
 * Unlike a trip board it never resets and needs nobody to be driving, so it is
 * the one place a whole collection gets compared with anyone else's. It refreshes
 * on every sync rather than being polled, which makes it as fresh as the last
 * time this phone had signal - so it says when that was.
 */

/** The server's reply leads with the status; see `postJson` in syncClient.ts. */
function addFriendError(message: string): string {
  if (message.includes('your own code')) return 'That is your own code. Send it to a mate instead.';
  if (message.startsWith('404')) return 'No one has that code. Check it and try again.';
  if (message.startsWith('409')) {
    return message.includes('They already')
      ? 'They already have the most friends allowed.'
      : 'You already have the most friends allowed. Remove one to add another.';
  }
  if (message.startsWith('429')) return 'Too many attempts. Wait a minute and try again.';
  return 'Could not add that friend. Are you online?';
}

export function FriendsCard() {
  const { friends, publicId, sync, ensureFriendCode, addFriend, removeFriend } = useGame();
  const [code, setCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [managing, setManaging] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // The code never changes, so once fetched it is cached and shown with no
  // signal at all. Until then, each successful sync is proof there is signal to
  // try again with - which is why lastSyncedAt is a dependency it never reads.
  useEffect(() => {
    if (friends.code) return;
    ensureFriendCode().catch(() => {
      // Offline, most likely. The pending line already says so.
    });
  }, [friends.code, sync.lastSyncedAt, ensureFriendCode]);

  const others = friends.board.filter((entry) => entry.publicId !== publicId);

  const copyCode = async () => {
    if (!friends.code) return;
    try {
      await navigator.clipboard.writeText(formatCodeForDisplay(friends.code));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the code is on screen to read out anyway.
    }
  };

  const handleAdd = async () => {
    setAddError(null);

    if (!normaliseFriendCode(code)) {
      setAddError('That is not a valid friend code. They look like GALAH-ESKY-42.');
      return;
    }

    setAdding(true);
    try {
      await addFriend(code);
      setCode('');
    } catch (error) {
      setAddError(addFriendError(error instanceof Error ? error.message : String(error)));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (friendId: string) => {
    if (confirming !== friendId) {
      setConfirming(friendId);
      return;
    }
    setRemoveError(null);
    try {
      await removeFriend(friendId);
    } catch {
      setRemoveError('Could not remove them. Check your signal and try again.');
    } finally {
      setConfirming(null);
    }
  };

  return (
    <div className="card">
      <h2>Friends</h2>
      <p>
        Lifetime points, for everyone you&rsquo;ve swapped codes with. Once a mate
        enters your code, you&rsquo;re on each other&rsquo;s board.
      </p>

      {friends.board.length > 1 ? (
        <>
          <Leaderboard entries={friends.board} meId={publicId} />
          {friends.at > 0 && (
            <p className="lb-updated">
              Updated{' '}
              {new Date(friends.at).toLocaleTimeString('en-AU', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          )}
        </>
      ) : (
        <p>No friends yet. Send someone your code and you&rsquo;ll both show up here.</p>
      )}

      <div style={{ height: 12 }} />
      {friends.code ? (
        <>
          <div className="code-display" style={{ fontSize: '1rem' }}>
            {formatCodeForDisplay(friends.code)}
          </div>
          <div style={{ height: 8 }} />
          <button type="button" className="btn" onClick={() => void copyCode()}>
            {copied ? 'Copied' : 'Copy my friend code'}
          </button>
        </>
      ) : (
        <div className="status-line">
          <span className="status-dot pending" />
          <span>Your friend code appears once you have signal</span>
        </div>
      )}

      <div style={{ height: 16 }} />
      <input
        className="search"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        placeholder="galah-esky-42"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        aria-label="Friend code"
      />
      {addError && <p className="field-error">{addError}</p>}
      <div style={{ height: 10 }} />
      <button
        type="button"
        className="btn"
        onClick={() => void handleAdd()}
        disabled={adding || code.trim().length === 0}
      >
        {adding ? 'Adding…' : 'Add friend'}
      </button>

      {others.length > 0 && (
        <>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setManaging(!managing);
              setConfirming(null);
            }}
          >
            {managing ? 'Done' : 'Manage friends'}
          </button>
          {managing && (
            <ul className="friend-list" aria-label="Your friends">
              {others.map((entry) => (
                <li key={entry.publicId}>
                  <span className="friend-name">{entry.displayName}</span>
                  <button
                    type="button"
                    className="friend-remove"
                    onClick={() => void handleRemove(entry.publicId)}
                  >
                    {confirming === entry.publicId ? 'Tap again to remove' : 'Remove'}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {removeError && <p className="field-error">{removeError}</p>}
        </>
      )}
    </div>
  );
}
